import { Box, Icon, Menu, MenuItem, Tab, Tabs } from '@mui/material'
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './sf-app-nav.scss'

/**
 * Accessibility Notes (a11y)
 * --------------
 * use NVDA screenreader to verify behavior
 *   - Should announce "<title> Tab selected 1 of 9"
 *   - For MenuGroups, announce "<title> Tab collapsed submenu 2 of 9"
 *   - For Submenu items, announces "<title> (checked | not checked) 1 of 2"
 *     - unfortunately, we can't use "aria-selected" with role="menuitem", so we use "aria-checked" with role="menuitemcheckbox"
 * Keyboard Nav - IMPORTANT!!
 *   - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/menu_role#keyboard_interactions
 *   - Tab key into menu, then left/right arrow keys change focus on items within the menu
 *   - Tab key again tabs out, PAST the menu, to the next focusable thing
 *   - enter/space selects an item
 *   - for menugroup items, down arrow (in addition to enter/space) also selects/opens the submenu
 */

export interface AppNavMenuItem {
  title: string
  path: string
}

export interface AppNavMenuGroup {
  title: string
  items: AppNavMenuItem[]
}

export interface AppNavMenu {
  items: (AppNavMenuGroup | AppNavMenuItem)[]
}

interface AppNavProps {
  menu: AppNavMenu
}

function isMenuGroup(item: AppNavMenuGroup | AppNavMenuItem): boolean {
  return 'items' in item
}

export const SFAppNav: React.FC<AppNavProps> = ({ menu }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = location.pathname

  // Track which group's dropdown is currently open
  const [openGroupIndex, setOpenGroupIndex] = useState<number | null>(null)
  // Track anchor element for dropdown menu
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(
    null
  )

  // Define isItemSelected function
  const isItemSelected = (path: string) => {
    return currentPath === path || currentPath.startsWith(path)
  }

  const openMenuGroup = (groupIndex: number | null, anchorEl: HTMLElement) => {
    setOpenGroupIndex(groupIndex)
    setSubmenuAnchorEl(anchorEl)
  }

  const closeMenuGroup = () => {
    setOpenGroupIndex(null)
    setSubmenuAnchorEl(null)
  }

  const handleMenuItemClick = (path: string) => {
    void navigate(path)
    closeMenuGroup()
  }

  const handleCloseMenu = () => {
    closeMenuGroup()
  }

  // Find the currently selected tab index
  const getSelectedTabIndex = (): number => {
    // First check for exact path matches (more specific)
    for (let i = 0; i < menu.items.length; i++) {
      const item = menu.items[i]
      if (isMenuGroup(item)) {
        // For groups, check if any child item is selected
        const group = item as AppNavMenuGroup
        for (const menuItem of group.items) {
          if (currentPath === menuItem.path) {
            return i
          }
        }
      } else {
        // For direct menu items, check if the path matches exactly
        const menuItem = item as AppNavMenuItem
        if (currentPath === menuItem.path) {
          return i
        }
      }
    }

    // Then check for path starts with (less specific)
    for (let i = 0; i < menu.items.length; i++) {
      const item = menu.items[i]
      if (isMenuGroup(item)) {
        // For groups, check if any child item path is a prefix
        const group = item as AppNavMenuGroup
        for (const menuItem of group.items) {
          if (currentPath.startsWith(menuItem.path) && menuItem.path !== '/') {
            return i
          }
        }
      } else {
        // For direct menu items, check if the path is a prefix
        const menuItem = item as AppNavMenuItem
        // Special case for root path to avoid matching everything
        if (menuItem.path === '/' && currentPath === '/') {
          return i
        } else if (
          currentPath.startsWith(menuItem.path) &&
          menuItem.path !== '/'
        ) {
          return i
        }
      }
    }

    return 0 // Default to first tab if no match
  }

  const handleTabChange = (
    event: React.SyntheticEvent,
    newTabIndex: number
  ) => {
    const item = menu.items[newTabIndex]
    handleTabSelect(event.target as HTMLElement, newTabIndex)

    if (isMenuGroup(item)) {
      //don't navigate to the tab, just open the submenu
      event.preventDefault()
    }
  }

  /** tab select can happen many ways (click, tabchange, keyboard), but they all get funneled here */
  const handleTabSelect = (tabEl: HTMLElement, index: number) => {
    const item = menu.items[index]

    if (isMenuGroup(item)) {
      // If it's a group, always open the dropdown
      openMenuGroup(index, tabEl)
    } else {
      // If it's a direct menu item, navigate to its path
      // if a page uses subpaths, directly selecting the item should go straight to the page's root path, even when it's already selected
      // this is because we're not "changing"
      void navigate((item as AppNavMenuItem).path)
      closeMenuGroup()
    }
  }

  const handleTabClick = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    handleTabSelect(event.currentTarget, index)
  }

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    const item = menu.items[index]

    //ignore it it's not a group
    if (!isMenuGroup(item)) return

    // If it's a group, toggle the dropdown regardless of whether it's already selected
    // technically, MUI handles Enter/Space, but we include those here for consistency
    if (
      event.key === 'ArrowDown' ||
      event.key === 'Enter' ||
      event.key == 'Space'
    ) {
      event.preventDefault()
      handleTabSelect(event.currentTarget, index)
    }
  }

  const renderTabs = () => {
    return (
      <Tabs
        value={getSelectedTabIndex()}
        onChange={handleTabChange}
        className="sf-app-nav-tabs"
        aria-label="App Navigation"
      >
        {menu.items.map((item, index) => (
          <Tab
            // eslint-disable-next-line react-x/no-array-index-key
            key={index}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {item.title}
                {isMenuGroup(item) && (
                  <Box sx={{ display: 'flex', ml: 0.5 }}>
                    <Icon fontSize="small">
                      {openGroupIndex === index
                        ? 'expand_more'
                        : 'chevron_right'}
                    </Icon>
                  </Box>
                )}
              </Box>
            }
            className={openGroupIndex === index ? 'group-open' : ''}
            onClick={(e) => handleTabClick(e, index)}
            onKeyDown={(e) => handleTabKeyDown(e, index)}
            //important for screen reader a11y (says "collapsed / submenu")
            aria-haspopup={isMenuGroup(item) ? 'true' : undefined}
            aria-expanded={
              isMenuGroup(item)
                ? openGroupIndex === index
                  ? 'true'
                  : 'false'
                : undefined
            }
          />
        ))}
      </Tabs>
    )
  }

  const renderSubmenu = () => {
    if (openGroupIndex === null || !submenuAnchorEl) return null
    const item = menu.items[openGroupIndex]
    if (!isMenuGroup(item)) return null

    const group = item as AppNavMenuGroup

    return (
      <Menu
        className="sf-app-nav-submenu"
        anchorEl={submenuAnchorEl}
        open={true}
        onClose={handleCloseMenu}
        elevation={3}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '1px',
              minWidth: '150px',
            },
          },
          list: {
            sx: {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        }}
      >
        {group.items.map((menuItem, itemIndex) => (
          <MenuItem
            // eslint-disable-next-line react-x/no-array-index-key
            key={itemIndex}
            onClick={() => handleMenuItemClick(menuItem.path)}
            selected={isItemSelected(menuItem.path)}
            //important for screen reader a11y: calls out "checked".  There's no equivalent of aria-selected=true like there is for tabs
            role="menuitemcheckbox"
            aria-checked={isItemSelected(menuItem.path)}
            className={
              isItemSelected(menuItem.path) ? 'selected-menu-item' : ''
            }
          >
            {menuItem.title}
          </MenuItem>
        ))}
      </Menu>
    )
  }

  return (
    <div className="sf-app-nav">
      {renderTabs()}
      {renderSubmenu()}
    </div>
  )
}

export default SFAppNav
